import {
  useCustom,
  useCustomMutation,
  useGetIdentity,
  useTranslate,
} from '@refinedev/core';
import * as Sentry from '@sentry/react';
import { Button, message, Tooltip } from 'antd';
import { formatDuration, intervalToDuration } from 'date-fns';
import { enUS, ru } from 'date-fns/locale';
import React, { useEffect, useMemo, useState } from 'react';
import { IUser } from '../../providers';
import { ReviewSession } from '../../schema/be-graphql-generated';
import { graphqlErrorInfo } from '../../utils/graphqlErrors';

/**
 * E04 · the REVIEW SESSION control (built 2026-09-21) — the header's second session control,
 * beside the calendar (clinical) one. Founder decision 2026-07-18: two DISTINCT controls —
 * 'Start Review Session' (RTM, this) vs 'Start Clinical Session' (the L17/E17 calendar session,
 * relabeled for disambiguation).
 *
 * "Active state: 'End Review Session' + elapsed indicator replace the start button" (board,
 * `Client Activity Monitor · Review Session controls`). The active bracket is the CALLER's own
 * open one on the selected client (a colleague's open bracket on the same client is theirs —
 * the guard is per performer+subject); it is read from `reviewSessions(activeOnly)` on mount and
 * after every Start/End, so a bracket opened in another tab, or ended by the idle timeout while
 * this tab sat open, shows correctly on the next look. `errorNotification: false` — the caller
 * reports: a CODED refusal (already active / none active) is the server's own localized copy.
 */
export const REVIEW_SESSION_FIELDS = [
  'reviewSessionId',
  'subject',
  'performer',
  'performerName',
  'practiceId',
  'startedAt',
  'endedAt',
  'endedVia',
] as const;

const elapsedOf = (startedAt: string, locale: 'en' | 'ru'): string =>
  formatDuration(
    intervalToDuration({ start: new Date(startedAt), end: new Date() }),
    {
      format: ['days', 'hours', 'minutes'],
      locale: locale === 'ru' ? ru : enUS,
      zero: false,
    },
  ) || (locale === 'ru' ? '< 1 мин' : '< 1 min');

export const ReviewSessionControl: React.FC<{
  clientId: string;
  locale: 'en' | 'ru';
  className?: string;
  /**
   * E57 — the control lives in the header bar AND in the client page header (only the button moved; the same E04
   * commands): each place keeps its own testids (`<prefix>-button`, `<prefix>-elapsed`).
   */
  testIdPrefix?: string;
}> = ({ clientId, locale, className, testIdPrefix = 'cc-header-review-session' }) => {
  const translate = useTranslate();
  const { data: identity } = useGetIdentity<IUser>();
  const me = identity?.db?._id;

  // refine's `custom` answers the OPERATION's value under `data` (an array here), not the
  // response envelope — the same shape every other `useCustom` reader in the portal consumes
  const { data, refetch, isLoading } = useCustom<ReviewSession[]>({
    url: '',
    method: 'get',
    meta: {
      operation: 'reviewSessions',
      fields: [...REVIEW_SESSION_FIELDS],
      variables: {
        subject: { name: 'subject', type: 'ID!', value: clientId },
        activeOnly: { name: 'activeOnly', type: 'Boolean', value: true },
      },
    },
    queryOptions: { enabled: Boolean(clientId) },
  });

  const active = useMemo(
    () =>
      (data?.data ?? []).find((r) => r.performer === me && !r.endedAt) ?? null,
    [data, me],
  );

  const activeStartedAt = active?.startedAt;
  const [elapsed, setElapsed] = useState<string | undefined>();
  useEffect(() => {
    if (!activeStartedAt) {
      setElapsed(undefined);
      return undefined;
    }
    setElapsed(elapsedOf(activeStartedAt, locale));
    const handle = setInterval(
      () => setElapsed(elapsedOf(activeStartedAt, locale)),
      15_000,
    );
    return () => clearInterval(handle);
  }, [activeStartedAt, locale]);

  const { mutateAsync, isLoading: mutating } =
    useCustomMutation<ReviewSession>();

  const reportFailure = (error: unknown) => {
    const { code, message: serverMessage } = graphqlErrorInfo(error);
    if (code && serverMessage) {
      message.error(serverMessage);
      return;
    }
    message.error(translate('components.header.reviewSessionFailed'));
    Sentry.captureException(error);
  };

  const act = async () => {
    const operation = active ? 'endReviewSession' : 'startReviewSession';
    try {
      const res = await mutateAsync({
        url: '',
        method: 'post',
        values: {},
        errorNotification: false,
        meta: {
          operation,
          fields: [...REVIEW_SESSION_FIELDS],
          variables: {
            subject: { name: 'subject', type: 'ID!', value: clientId },
          },
        },
      });
      if (!res?.data) {
        // a null answer is a REFUSAL (the practice-client gate), never a save
        message.error(translate('components.header.reviewSessionFailed'));
      } else {
        message.success(
          translate(
            active
              ? 'components.header.reviewSessionEnded'
              : 'components.header.reviewSessionStarted',
          ),
          1,
        );
      }
    } catch (error) {
      reportFailure(error);
    } finally {
      await refetch();
    }
  };

  return (
    <Tooltip
      title={
        active ? undefined : translate('components.header.startReviewSession')
      }
    >
      <Button
        type={active ? 'primary' : 'default'}
        danger={Boolean(active)}
        loading={(Boolean(clientId) && isLoading) || mutating}
        onClick={act}
        className={className}
        data-testid={`${testIdPrefix}-button`}
        data-active={active ? 'true' : 'false'}
      >
        {active
          ? translate('components.header.endReviewSession')
          : translate('components.header.startReviewSession')}
        {active && elapsed ? (
          <span
            data-testid={`${testIdPrefix}-elapsed`}
            style={{ marginLeft: 8, fontWeight: 400 }}
          >
            {translate('components.header.reviewActiveElapsed', { elapsed })}
          </span>
        ) : null}
      </Button>
    </Tooltip>
  );
};
